/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes.
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Cart refresh for minicart
 */
function refreshCart() {
  fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
    async (response2) => {
      const cartContent = await response2.json();
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart:
              window.themeVariables.settings.cartType === "drawer" &&
              this.closest(".drawer") === null,
          },
        })
      );
    }
  );
}

/**
 * Handle Price Per Square Feet Filtering
 */
function handlePriceAndPricePerSqFtFilters() {
  const pricePerSquareFtFilters = document.querySelectorAll(
    '[name="filter.p.m.custom.price_per_sqft"]'
  );
  const rugsProductTypeFilter = document.querySelector(
    '[name="filter.p.product_type"][value="RUGS"]'
  );
  const priceInputFilters = document.querySelectorAll(
    ".price-range__input-group input"
  );

  // Handle of Price Filter
  if (priceInputFilters && rugsProductTypeFilter) {
    priceInputFilters.forEach((priceInputFilter) => {
      priceInputFilter.addEventListener("change", (e) => {
        if (e.target.value) {
          // Click checked price per square ft filters
          pricePerSquareFtFilters.forEach((pricePerSquareFtFilter) => {
            if (pricePerSquareFtFilter.checked) {
              pricePerSquareFtFilter.click();
            }
          });

          // Select Rug Product if not checked
          if (!rugsProductTypeFilter.checked) {
            rugsProductTypeFilter.click();
          }
        }
      });
    });
  }

  // Handle Selecting of Price Per Square Feet Filters
  if (pricePerSquareFtFilters) {
    pricePerSquareFtFilters.forEach((pricePerSquareFtFilter) => {
      pricePerSquareFtFilter.addEventListener("click", (e) => {
        // Clear price input filters when pricePerSquareFtFilter is clicked
        priceInputFilters.forEach((priceInputFilter) => {
          priceInputFilter.value = ""; // Clear the input field
        });
      });
    });
  }
}

const facetFilters = document.querySelector("facet-filters");
const facetCallback = function (mutationsList, observer) {
  handlePriceAndPricePerSqFtFilters();
};

const facetObserver = new MutationObserver(facetCallback);
const facetConfig = { attributes: true, childList: true, subtree: true };
if (facetFilters) {
  facetObserver.observe(facetFilters, facetConfig);
}

(async () => {
  const pagePath = window.location.pathname;
  const qsParams = new URLSearchParams(location.href.split("?")[1]);

  /**
   * CLEAR CART ON LOGOUT / IF LOGGED OUT
   * Note: Customer session (retail or trade) is not expected to time out / expire
   */
  const logoutLink = document.querySelector(".link-bar__link[data-no-instant]");
  if (logoutLink !== null) {
    logoutLink.addEventListener("click", function (event) {
      let cartCookieValue = getExpiredCartConfirmation();
      if (cartCookieValue === undefined) {
        event.preventDefault();
        deleteCookie("cart");
        logoutLink.click();
      } else {
        // Revert the setting of this window property
        //  - this is to avoid failing to clear the cart next time
        window.cartCookieExpiration = undefined;
      }
    });
  }

  function getExpiredCartConfirmation() {
    return window.cartCookieExpiration;
  }

  function setCookie(key) {
    document.cookie = key + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    // Also set expiration date where we can check it later
    window.cartCookieExpiration = "Expires=Thu, 01 Jan 1970 00:00:01 GMT";
  }

  function deleteCookie(name) {
    setCookie(name);
  }

  const loggedInB2B =
    document.getElementById("header-loggedin-mobile__b2b") ||
    document.getElementById("header-loggedin-desktop__b2b");

  if (loggedInB2B !== null) {
    removeNonB2BSamples();
  }

  // get current cart
  async function removeNonB2BSamples() {
    let updates = {};
    let cartContents = await fetch(window.Shopify.routes.root + "cart.js")
      .then((response) => response.json())
      .then((data) => {
        return data;
      });

    // check cart for non-B2B samples
    if (cartContents && cartContents.items) {
      Array.from(cartContents.items, (item) => {
        if (item.sku.endsWith("_S")) {
          updates[item.variant_id] = 0;
        }
      });
    }

    // remove any non-B2B samples
    if (Object.keys(updates).length > 0) {
      fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      })
        .then(async (response) => {
          let responseJson = await response.json();
          //refresh cart
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: responseJson,
              },
            })
          );
          // show message
          document.documentElement.dispatchEvent(
            new CustomEvent("cart-notification:show", {
              bubbles: true,
              detail: {
                status: "info",
              },
            })
          );
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  }

  // create a debounce method
  const debounce = (mainFunction, delay) => {
    let timer;

    return function (...args) {
      clearTimeout(timer);

      timer = setTimeout(() => {
        mainFunction(...args);
      }, delay);
    };
  };

  /*
   * HOMEPAGE HERO BANNER LOGIC
   */

  // Homepage banner mobile logic
  function homepageHeroBannerMobileFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );

    if (!homepageBanner) return;

    function setMobileHeights() {
      let bannerContent = document.querySelector(
        ".shopify-section--hero-banner .content-box"
      );
      if (!bannerContent) {
        return;
      }
      let bannerContentHeight = bannerContent.offsetHeight - 1; // slight overlapp to prevent whitespace
      let calculatedAssetHeight = `calc(100vh - ${bannerContentHeight}px`;

      homepageBanner.style.setProperty("--asset-height", calculatedAssetHeight);
    }

    window.addEventListener("DOMContentLoaded", function () {
      if (window.innerWidth <= 768) {
        setMobileHeights();
      }
    });

    window.addEventListener("resize", function () {
      if (window.innerWidth <= 768) {
        setMobileHeights();
      }
    });
  }

  // Homepage banner fixed position logic
  function homepageHeroBannerFixedPositionFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );
    const fixedSlider = document.querySelector(
      ".shopify-section--hero-banner .fixed-slideshow"
    );

    if (!(homepageBanner && fixedSlider)) return;

    // update slideshow visibility based on scroll position
    function updateHomepageBannerVisibilty() {
      let scrollY = window.scrollY;
      let slideshowHeight = fixedSlider.offsetHeight;
      let sliderInViewport = scrollY <= slideshowHeight;
      let sliderIsVisible = fixedSlider.classList.contains("slideshow-visible");
      let sliderIsHidden = fixedSlider.classList.contains("slideshow-hidden");

      if (!sliderInViewport && sliderIsVisible) {
        hideSlider(fixedSlider);
      } else if (sliderInViewport && sliderIsHidden) {
        showSlider(fixedSlider);
      }
    }

    function hideSlider(slider) {
      slider.classList.add("slideshow-hidden");
      slider.classList.remove("slideshow-visible");
    }

    function showSlider(slider) {
      slider.classList.remove("slideshow-hidden");
      slider.classList.add("slideshow-visible");
    }

    const debouncedVisibility = debounce(updateHomepageBannerVisibilty, 50);

    document.addEventListener("scroll", function () {
      debouncedVisibility();
    });
  }

  // Homepage banner promobar logic
  function homepageHeroBannerPromobarFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );
    const homepageSection = document.querySelector(
      ".shopify-section--hero-banner .section"
    );
    const promobar = document.querySelector(
      ".shopify-section--announcement-bar"
    );

    if (!(homepageBanner && promobar)) return;

    function updatePromobarVisibility() {
      let scrollY = window.scrollY;
      let slideshowHeight = homepageSection.offsetHeight;
      let sliderInViewport = scrollY <= slideshowHeight;
      let promobarIsVisible = promobar.classList.contains("promobar-visible");
      let promobarIsHidden = promobar.classList.contains("promobar-hidden");

      if (sliderInViewport && promobarIsVisible) {
        hidePromobar();
      } else if (!sliderInViewport && promobarIsHidden) {
        showPromobar();
      }
    }

    function hidePromobar() {
      promobar.classList.add("promobar-hidden");
      promobar.classList.remove("promobar-visible");
    }

    function showPromobar() {
      promobar.classList.remove("promobar-hidden");
      promobar.classList.add("promobar-visible");
      promobar.classList.add("fade-in-top");
    }

    const debouncedPromobarVisibility = debounce(updatePromobarVisibility, 100);

    promobar.classList.add("promobar-hidden");

    document.addEventListener("scroll", function () {
      debouncedPromobarVisibility();
    });
  }

  // Homepage nav bar logic
  function homepageHeroBannerNavFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );
    const homepageSection = document.querySelector(
      ".shopify-section--hero-banner .section"
    );
    const mainHeader = document.querySelector(".main-header");
    const storeHeader = document.querySelector("store-header");
    const transparentHeader = document.querySelector(
      ".transparent-main-header"
    );

    if (!(homepageBanner && transparentHeader)) return;

    function updateHeaderVisibility() {
      let scrollY = window.scrollY;
      let slideshowHeight = homepageSection.offsetHeight;
      let sliderInViewport = scrollY <= slideshowHeight;
      let transparentHeaderIsVisible = transparentHeader.classList.contains(
        "transparent-main-header-visible"
      );
      let transparentHeaderIsHidden = transparentHeader.classList.contains(
        "transparent-main-header-hidden"
      );

      if (sliderInViewport && transparentHeaderIsHidden) {
        hideOriginalHeader();
      } else if (!sliderInViewport && transparentHeaderIsVisible) {
        showOriginalHeader();
      }
    }

    function hideOriginalHeader() {
      mainHeader.classList.add("main-header-hidden");
      mainHeader.classList.remove("main-header-visible");

      mainHeader.classList.remove("fade-out-top");
      mainHeader.classList.add("fade-in-top");

      transparentHeader.classList.add("transparent-main-header-visible");
      transparentHeader.classList.remove("transparent-main-header-hidden");
    }

    function showOriginalHeader() {
      mainHeader.classList.remove("main-header-hidden");
      mainHeader.classList.add("main-header-visible");

      storeHeader.classList.remove("fade-out-top");
      storeHeader.classList.add("fade-in-top");

      mainHeader.classList.remove("fade-out-top");
      mainHeader.classList.add("fade-in-top");

      transparentHeader.classList.remove("transparent-main-header-visible");
      transparentHeader.classList.add("transparent-main-header-hidden");
    }

    const debouncedPromobarVisibility = debounce(updateHeaderVisibility, 100);

    document.addEventListener("scroll", function () {
      debouncedPromobarVisibility();
    });
  }

  homepageHeroBannerMobileFunctionality();
  homepageHeroBannerFixedPositionFunctionality();
  homepageHeroBannerPromobarFunctionality();
  homepageHeroBannerNavFunctionality();

  /**
   * PRODUCT PAGE RELATED CUSTOMIZATIONS ONLY
   */
  if (!pagePath.startsWith("/products/")) {
    return;
  }

  function selectFirst(inputListSelector, inputSelector) {
    let inputs = document.querySelectorAll(inputListSelector);
    let hasChecked = false;

    if (inputs.length == 0) {
      return;
    }

    Array.from(inputs).forEach(function (radio) {
      if (radio.checked) {
        hasChecked = true;
      }
    });

    if (!hasChecked) {
      document.querySelector(inputSelector).click();
    }
  }

  // Click first available variant for sample sizes
  const orderSampleButton = document.querySelector("button#SampleRug");
  if (orderSampleButton) {
    // if sample option exists click first available variant swatch on drawer open
    orderSampleButton.addEventListener("click", function () {
      // when user opens Sample Drawer trigger click on first color and size if not already selected
      const colorInputs = ".sample-rug__drawer .color-swatch-list .color-swatch__radio";
      const sizeInputs = ".sample-rug__drawer .block-swatch-list .block-swatch__radio";
      const colorSelector = ".sample-rug__drawer .color-swatch-list .color-swatch__item";
      const sizeSelector = ".sample-rug__drawer .block-swatch-list .block-swatch__item";

      selectFirst(colorInputs, colorSelector);
      selectFirst(sizeInputs, sizeSelector);
    });
  }

  // Get main product
  const mainProduct = await getProduct();

  // Set global variables
  const leadTimePrefix = "Expected to ship in";

  // Sku Update
  document.addEventListener("variant:changed", function (event) {
    let sku = event.detail.variant["sku"].split("_")[0];
    sku = sku.replaceAll("-", "%20");

    // Spce Sheet link - Update spec sheet url
    let specSheetLink = document.getElementById("spec-sheet-link");
    if (specSheetLink && event.detail.variant["sku"]) {
      let newURL =
        specSheetLink.getAttribute("data-base-url") +
        sku +
        "/spec-sheet?appid=0";
      specSheetLink.setAttribute("href", newURL);
      specSheetLink.classList.remove("hidden");
    } else if (specSheetLink && !event.detail.variant["sku"]) {
      specSheetLink.classList.add("hidden");
    }

    // Hubspot Inquiry Form - update product_inquiry field
    const productInquiryInput = document.querySelector(
      'input[name="product_inquiry"]'
    );
    if (productInquiryInput) {
      productInquiryInput.value = sku;
      productInquiryInput.dispatchEvent(new Event("input", { bubbles: true }));
    }

    // Inquiry drawer SKU
    const productSkuLabel = document.querySelector(
      '.product-inquiry__info [data-inquiry-sku-number]'
    );

    if (productSkuLabel) {
      productSkuLabel.textContent = sku;
    }
  });

  // Get querystring, then variant ID from parameters, if exists
  // Set the currentVariant if there is a variant ID in the params
  let currentVariant = undefined;
  let variantId = !!qsParams ? qsParams.get("variant") : null;
  Object.keys(mainProduct.variants).forEach((i) => {
    if (parseInt(mainProduct.variants[i].id) === parseInt(variantId)) {
      currentVariant = mainProduct.variants[i];
    }
  });

  // Update Product Details hiding listings without values
  (function () {
    // Get Product Details Element
    const buttonsSelector =
      ".product-tabs > .product-tabs__content > " +
      ".product-tabs__tab-item-wrapper > button.collapsible-toggle.heading";
    const productTabsContentButtons =
      document.querySelectorAll(buttonsSelector);
    const productDetailsButton = Array.from(productTabsContentButtons).find(
      (el) => el.textContent.includes("Product Details")
    );
    const productDetails = productDetailsButton.parentElement.querySelector(
      ".product-tabs__tab-item-content.rte"
    );

    // Get rendered Product metafield data elements
    const isTableData = productDetails.getElementsByTagName("table").length > 0;
    const selectTag = isTableData ? "tr" : "li";
    const potentialElementsArray = Array.from(
      productDetails.getElementsByTagName(selectTag)
    );
    const parentElements = potentialElementsArray.filter(
      (el) => el.getElementsByTagName(selectTag).length > 0
    );
    const topLevelRecords = potentialElementsArray.filter((el) => {
      for (const parentElement of parentElements) {
        if (!parentElement.isSameNode(el) && parentElement.contains(el)) {
          return false;
        }
      }
      return true;
    });
    const metafieldElements = isTableData
      ? topLevelRecords.filter((el) => el.getElementsByTagName("th").length < 1)
      : topLevelRecords;
    const parseValueAndLabel = (el) =>
      el.innerText.split(":").map((item) => item.trim()); // TODO expand handling to support table or other potential list metafield formats
    const removeEmptyMetaField = (el) => (el.hidden = true); // Update to delete node if desired

    // Hide empty metafield Elements
    for (const metafieldElement of metafieldElements) {
      const [field, fieldValue] = parseValueAndLabel(metafieldElement);
      const parts = metafieldElement.textContent.split(":");

      // Check if there are at least two parts (text before and after ":")
      if (parts.length >= 2) {
        // The item has text after ":"
        // You can access it using parts[1] (trim() is used to remove any leading/trailing spaces)
        const textAfterColon = parts[1].trim();

        // Check if textAfterColon is not empty
        if (textAfterColon.length > 1) {
          // Do something with the item that has text after ":"
          metafieldElement.removeAttribute("hidden");
        } else {
          metafieldElement.setAttribute("hidden", false);
        }
      }
    }
  })();

  /* Check (reliably) for a request from cart to a custom size variant */
  if (variantId !== null) {
    // Get the variant from the mainProduct data with the ID from the querystring
    let currentVariantName = "";
    Object.keys(mainProduct.variants).forEach((i) => {
      if (parseInt(mainProduct.variants[i].id) === parseInt(variantId)) {
        currentVariantName = mainProduct.variants[i].name;
      }
    });

    if (currentVariantName.includes("Custom - ")) {
      /* Custom size variant logic (onload only) */
      // Redirect to the parent product
      console.log(
        "redirecting from custom variant " +
          currentVariantName +
          " to main PDP: " +
          location.href.split("?")[0]
      );
      location.replace(location.href.split("?")[0]);
    }
  }

  // Show PDP Product SKU without variant variations after "variant:changed"
  const productSkuNumber = document.querySelector(
    ".product__info > product-meta > div > div > " +
      "[data-product-sku-container] > [data-product-sku-number]"
  );
  if (productSkuNumber) {
    const skuObserver = new MutationObserver((mutationList, observer) => {
      if (!productSkuNumber.innerHTML.includes("_")) {
        return;
      }
      productSkuNumber.innerHTML = productSkuNumber.innerHTML
        .split("_")
        .shift();
    });
    skuObserver.observe(productSkuNumber, { childList: true });
  }

  const productForm = getProductForm(mainProduct);
  if (productForm === null) {
    return;
  }

  // Store "variant:changed" price updates
  const variantPricing = {
    price: null,
    compare_at_price: null,
    setPrice: function (price) {
      this.price = price;
      return this;
    },
    setCompareAtPrice: function (price) {
      this.compare_at_price = price;
      return this;
    },
  };

  const getCustomPricing = (currentVariantPrice) => {
    const productMetaContainer = document.querySelector('product-meta')
    let productType, customStartingPrice;
    let customPrice = 0;

    if(productMetaContainer) {
      productType = productMetaContainer.dataset.productType;
      customStartingPrice = productMetaContainer.dataset.customStartingPrice;
    }

    if(productType === "Broadloom" && customStartingPrice) {
      customPrice = parseFloat(customStartingPrice) * (isB2b ? 100 - b2bDiscount:100);
    } else {
      customPrice = currentVariantPrice || 0;
    }
    
    return customPrice;
  }

  let topY = 0;

  document.addEventListener('scroll', () => { 
    const scrollPos = document.body.getBoundingClientRect();
    topY = Math.abs(scrollPos.y);
  });

  document.addEventListener("variant:changed", (event) => {
    const currentScrollPos = Math.abs(document.body.getBoundingClientRect().y);
    // Compare current scroll position with topY
    if (currentScrollPos !== topY) {
      // Scroll back to topY
      window.scrollTo({
        top: topY,
        behavior: 'instant',
      });
    }

    // Update lead time on variant change
    let thisVariant = event.detail?.variant;

    variantPricing.setPrice(getCustomPricing(thisVariant?.price) || 0);
    variantPricing.setCompareAtPrice(thisVariant?.compare_at_price || 0);

    // Update lead time per variant selected
    if (thisVariant !== undefined) {
      // Update lead time for the selected variant
      let leadTimesDataEl = document.querySelector(".lead-time-data");
      let leadTimeDisplayEl = document.querySelector(".lead-time-label");
      let leadTimesData =
        leadTimesDataEl !== undefined ? leadTimesDataEl.innerHTML : "";
      let leadTimes = leadTimesData.split("|");
      let leadTimeInput = document.querySelector(
        'input[name="properties[_leadTime]"]'
      );

      Object.keys(leadTimes).forEach((i) => {
        if (thisVariant !== undefined) {
          let variantLeadTime = leadTimes[i].split(":");
          if (parseInt(variantLeadTime[0]) === parseInt(thisVariant.id)) {
            if(leadTimeDisplayEl) {
              if (variantLeadTime[1] === "") {
                leadTimeDisplayEl.innerHTML = "";
                leadTimeInput.setAttribute("value", "");
              } else {
                leadTimeDisplayEl.innerHTML =
                  leadTimePrefix + " " + variantLeadTime[1];
                leadTimeInput.setAttribute("value", variantLeadTime[1]);
              }
            }
          }
        }
      });
    }
  });

  // "Order a Sample" updates on variant change
  document.addEventListener("variant:changed", (event) => {
    event.preventDefault();
    updateVariantData();
  });

  const submitButton = getFormSubmitButton(productForm);
  const hasCustomOption = document.querySelector(".custom-size-selector");
  const hasProductInquiryButton = document.getElementById("product-inquiry-button");
  const currentColorChecked = document.querySelector('.color-swatch__radio[checked=checked][data-initial-selection]')  
  const isB2bEl = document.querySelector(".customer-type-data");
  const isB2b = isB2bEl !== undefined ? isB2bEl.innerHTML === "true" : false;
  const b2bDiscountEl = document.querySelector(".b2b-discount-data");
  const b2bDiscount =
    b2bDiscountEl !== undefined ? parseInt(b2bDiscountEl.innerHTML) : 0;


  function validateSelections() {
    setTimeout(
      function() {
        const currentColor = document.querySelector('.product-form__option-info .product-form__option-value');
        const currentSizeContainer = document.querySelector('.block-swatch-list--size');
        const currentSize = document.querySelector('.block-swatch-list--size .block-swatch__radio[checked=checked]:not([data-initial-selection])');
        const colorSwatches = document.querySelector('.product-form .color-swatch-list')
        const errColor = document.getElementsByClassName("alert color")[0];
        const errSize = document.getElementsByClassName("alert size")[0];

        if (currentColor.innerText.length < 1) {
          errColor.innerText = "Please select a color";
        } else {
          errColor.innerHTML = "";
        }

        if (currentSize == null) {
          errSize.innerText = "Please select a size";
        } else {
          errSize.innerHTML = "";
        }
      }, 0
    );
  }

  validateSelections();

  if(hasProductInquiryButton){
    currentColorChecked.addEventListener('click', async (event) =>{
      if(event.target.hasAttribute('data-initial-selection')){
        const currentColorLabel = document.querySelector('.product-form__option-selector[data-selector-type=color] .product-form__option-value');

        currentColorLabel.innerText = event.target.value;
        await updateVariantData();
      }
    });
  } else if(hasCustomOption !== null){
    if(currentColorChecked){
      currentColorChecked.addEventListener('click', async (event) =>{
        if(event.target.hasAttribute('data-initial-selection')){
          const currentColorLabel = document.querySelector('.product-form__option-selector[data-selector-type=color] .product-form__option-value');

          currentColorLabel.innerText = event.target.value;
          await updateVariantData();
          atcOriginaPriceSpanUpdateCustomFirstVariant();
          validateCustomDimensionsInput();
        }
      });
    }

    function atcOriginaPriceSpanUpdateCustomFirstVariant() { 
      const productMeta = document.querySelector('product-meta');
      const productType = productMeta.dataset.productType;
      const startingPrice = parseFloat(productMeta.dataset.customStartingPrice);  
      const pdpPriceExtract = pdpPriceElement.innerText.match(/\$([0-9]+(?:\.[0-9]+)?)/);
      const currentPrice = pdpPriceExtract ? parseFloat(pdpPriceExtract[1]) * 100 : null;
      let newTotalPrice = getCustomPricing(startingPrice);
      let sizeSelectionLocal = localStorage.getItem("sizeSelection");
      let storedSize = null;

      if(sizeSelectionLocal){
        storedSize = JSON.parse(sizeSelectionLocal);
      }

      if(productType === 'Broadloom' && currentPrice <= newTotalPrice){
        // If no set size is selected, there is no need to update span on first variant click.
        if(storedSize === null || storedSize.total_area_inches === 0) {
          pdpPriceElement.innerHTML = `${formatMoney(newTotalPrice)} USD`;
        }
      }
    }
  } else if (hasCustomOption == null) {
    /**
     * STANDARD SIZE RELATED CUSTOMIZATIONS ONLY
     */
    const pristineInput = document.querySelector(".pristine-treatment__input");
    const pristinePrice = document.querySelector(".pristine-price");
    const rugPadInput = document.querySelector(".rug-pad__input");
    const rugPadPrice = document.querySelector(".rug-pad-price");
    const priceList = document.querySelector("[data-product-price-list]");
    const outerPriceElements = document.querySelectorAll(
      ".price-list > .price"
    );
    const pristineCheck = document.querySelector(
      '[type="checkbox"][name="properties[Add pristine rug treatment]"]'
    );
    const rugPadCheck = document.querySelector(
      '[type="checkbox"][name="properties[Add compatible rug pad]"]'
    );
    const pdpPriceElement = document.querySelector(
      ".price-list span.price.price--large .price-only"
    );
    const atcButton = document.querySelector("#AddToCart .loader-button__text");

    await updateAddOnPrices();
    await atcPriceSpanUpdate();

    pristineCheck.addEventListener("change", () => {
      atcPriceSpanUpdate();
      toggleCompareAndCustomClassHide();
    });
    rugPadCheck.addEventListener("change", () => {
      atcPriceSpanUpdate();
      toggleCompareAndCustomClassHide();
    });

    function getSelectedSize() {
      return document
        .querySelector(".block-swatch__radio:checked+.block-swatch__item")
        .innerHTML.toLowerCase();
    }

    function getSelectedColor() {
      return document
        .querySelector(
          ".color-swatch__radio:checked+.color-swatch__item>.visually-hidden"
        )
        .innerHTML.toLowerCase();
    }

    async function getAddOnProduct(type) {
      const types = {
        rug: "rug-pad",
        pristine: "pristine-treatment",
      };
      if (!types.hasOwnProperty(type)) {
        return false;
      }
      const lineItem = document.querySelector(`.${types[type]}__line-item`);
      if (!lineItem) {
        return false;
      }
      const productPath = lineItem.getAttribute(`data-${type}-product`);
      const response = await fetch(`${productPath}.js`);
      return await getResponseJson("getAddOnProduct", response);
    }

     function getAddOnVariant(product) {
      // Safety check: ensure product and variants exist
      if (!product || !product["variants"] || !Array.isArray(product["variants"])) {
        console.warn('getAddOnVariant: Invalid product or variants data', product);
        return null;
      }
      
      const thisVar = product["variants"].find((variant) =>
        variant["title"].toLowerCase().includes(getSelectedSize())
      );
      
      // Safety check for mainProduct.variants
      if (mainProduct && mainProduct.variants) {
        Object.keys(mainProduct.variants).forEach((i) => {
          currentVariantName = mainProduct.variants[i].name;
        });
      }
      
      return thisVar;
    }

    function getProductVariant(product) {
      const thisVar = product["variants"].find((variant) => {
        return (
          variant["title"].toLowerCase().includes(getSelectedSize()) &&
          variant["title"].toLowerCase().includes(getSelectedColor())
        );
      });
      Object.keys(mainProduct.variants).forEach((i) => {
        currentVariantName = mainProduct.variants[i].name;
      });
      return thisVar;
    }

    async function updateAddOnPrice(product, priceElement) {
      // go through variants
      // find variant whose name includes selected size
      //  - get variant price
      //  - add variant price to base
      //  - add variant price to line item
      const addOnVariant = await getAddOnVariant(product);

      if (!addOnVariant) return;

      const addOnVariantPrice = addOnVariant["price"] / 100;
      priceElement.innerHTML = addOnVariantPrice.toFixed(2);
    }

    async function returnAddOnPrice(product) {
      const addOnVariant = await getAddOnVariant(product);
      const addOnVariantPrice = addOnVariant["price"] / 100;
      return addOnVariantPrice.toFixed(2);
    }

    async function returnProductPrice(product) {
      const productVariant = await getProductVariant(product);
      const productVariantPrice = productVariant["price"] / 100;
      return productVariantPrice.toFixed(2);
    }

    async function returnProductAvailability(product) {
      const productVariant = await getProductVariant(product);
      const availability   = productVariant["available"];
      return availability;
    }

    async function updateAddOnPrices() {
      const rugProduct = await getAddOnProduct("rug");
      updateAddOnPrice(rugProduct, rugPadPrice);

      const pristineProduct = await getAddOnProduct("pristine");
      if (pristineProduct) {
        updateAddOnPrice(pristineProduct, pristinePrice);
      }
    }

    function atcOriginaPriceSpanUpdate() {
      const originalPriceCompare = document.querySelector('[data-original-price-compare]');
      const priceListCompare = priceList.querySelector('.price.price--compare');
      if(!originalPriceCompare) return;

      const formatOriginalPriceCompare = originalPriceCompare.dataset.originalPriceCompare;
      priceListCompare.innerText = `${formatMoney(formatOriginalPriceCompare)} USD`;
    }

    async function atcPriceSpanUpdate() {
      let productPath = window.location.pathname;
      let response = await fetch(`${productPath}.js`);
      let productJson = await getResponseJson("product", response);
      let prodPrice = await returnProductPrice(productJson);
      let total = parseFloat(prodPrice);
      let prodAvailability = await returnProductAvailability(productJson);
      let isInitialSelection = document.querySelectorAll('[data-initial-selection]');
      let newPriceString = '';

      if (rugPadInput.checked) {
        const rugProduct = await getAddOnProduct("rug");
        let rPrice = await returnAddOnPrice(rugProduct);
        total += parseFloat(rPrice);
      }

      if (pristineInput.checked) {
        const pristineProduct = await getAddOnProduct("pristine");
        if (pristineProduct) {
          let pPrice = await returnAddOnPrice(pristineProduct);
          total += parseFloat(pPrice);
        }
      }

      newPriceString += `${formatMoney(total * 100)} USD`;
      if (isInitialSelection.length) {
        const lowestPricedItem = productJson.variants
        .filter(item => item.available && !item.title.toLowerCase().includes("sample"))
        .sort((a, b) => a.price-b.price) || null;
        if(prodAvailability){
          atcButton.disabled = false;

          if(lowestPricedItem) {
            pdpPriceElement.innerHTML = `Starting at ${formatMoney(lowestPricedItem[0].price)} USD`;
            atcButton.innerHTML = `Add To Cart`;
          } else {
            pdpPriceElement.innerHTML = newPriceString;
            atcButton.innerHTML = `Add To Cart - ${newPriceString}`;
          }
        } else {
          atcButton.disabled = true;
          atcButton.innerHTML = `Out of stock`;
        }
      } else {
        if(prodAvailability){
          atcButton.disabled = false;
          pdpPriceElement.innerHTML = newPriceString;
          atcButton.innerHTML = `Add To Cart - ${newPriceString}`;
        } else {
          atcButton.disabled = true;
          atcButton.innerHTML = `Out of stock`;
        }
      }
    }

    async function getCartItemCount() {
      // Get the cart object
      let cart = JSON.parse(localStorage.getItem("cart"));
      return cart !== null ? cart.items.length : 0;
    }

    document.addEventListener("variant:changed", updateAddOnPrices);
    document.addEventListener("variant:changed", () => {
      atcPriceSpanUpdate();
      toggleCompareAndCustomClassHide();
    });
    const standardRugPriceListMutationObserver =
      addPostVariantChangedEventListener(
        priceList,
        outerPriceElements,
        priceListIsTargetMutation,
        priceListReAddNode
      );

    if(currentColorChecked){
      currentColorChecked.addEventListener('click', async (event) =>{
        if(event.target.hasAttribute('data-initial-selection')){
          const currentColorLabel = document.querySelector('.product-form__option-selector[data-selector-type=color] .product-form__option-value');
          
          currentColorLabel.innerText = event.target.value;
          await updateVariantData();
          atcOriginaPriceSpanUpdate();
          atcPriceSpanUpdate();
        }
      });
    }
    
    submitButton.addEventListener("click", async function (event) {
      if (event.preventLoop) {
        return;
      }

      const { target } = event;
      const originalEvent = new event.constructor(event.type, event);
      originalEvent.preventLoop = true;
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();

      const addOns = [];
      const uniqueString = Math.random().toString(16).slice(2);
      await updateAddOnPrices();

      // Prepare selected add ons
      if (pristineInput.checked) {
        const pristineProduct = await getAddOnProduct("pristine");
        const pristineVariant = getAddOnVariant(pristineProduct);
        addOns.push(pristineVariant);
      }
      if (rugPadInput.checked) {
        const rugPadProduct = await getAddOnProduct("rug");
        const rugPadVariant = getAddOnVariant(rugPadProduct);
        addOns.push(rugPadVariant);
      }

      // Add to Cart
      const formData = new FormData(productForm);
      const qtyElement = document.querySelector('[name="quantity"]');
      let qty = 1;
      if (qtyElement !== undefined) {
        qty = qtyElement.value;
      }

      let leadTime = await getLeadTime();
      leadTime = leadTime !== null ? leadTime.replace(leadTimePrefix, "") : "";

      let sortOrder = (await getCartItemCount()) + 1;
      const lineItemProperties = {};
      if (rugPadInput.checked) {
        sortOrder++;
      }
      if (pristineInput.checked) {
        sortOrder++;
      }
      if (leadTime !== "") {
        lineItemProperties["Expected to ship in"] = leadTime;
      }

      // Add sort order and tie add ons together with this product with a line item property
      //  containing a unique identifier to keep like items from grouping in cart
      const cartItemDetails = formData.get("id") + "_" + uniqueString;
      lineItemProperties._cartItemDetails = sortOrder++ + "_" + cartItemDetails;

      const items = [];
      items.push({
        id: formData.get("id"),
        quantity: qty,
        properties: lineItemProperties,
      });

      // May need to fix the sortOrder to be accurate
      //  This is currently the same sortOrder for both add ons
      const addOnLineItemProperties = {};

      const currentVariant = await getActiveVariant(mainProduct, formData);
      addOnLineItemProperties["Add-On for"] = currentVariant.name;
      addOnLineItemProperties._cartItemDetails =
        sortOrder++ + "_" + cartItemDetails;

      addOns.forEach((addOn) =>
        items.push({
          id: addOn.id,
          quantity: qty,
          properties: addOnLineItemProperties,
        })
      );

      const itemData = { items };
      console.log(`itemData:`, itemData);

      const result = fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(itemData),
      }).then(function (response) {
        let responseJson = getResponseJson(
          `standard rug addProduct fetch response`,
          response
        );
        // theme.js post-/cart/add dispatch event code
        productForm.dispatchEvent(
          new CustomEvent("variant:added", {
            bubbles: true,
            detail: {
              variant: responseJson.hasOwnProperty("items")
                ? responseJson["items"][0]
                : responseJson,
            },
          })
        );
        rebuildCart(productForm);

        return responseJson;
      });

      toggleFormSubmitButtons(productForm, true);

      rebuildCart(productForm);
      showAddToCartNotification();

      return result;
    });

    return;
  }

  /**
   * CUSTOM SIZE RELATED CUSTOMIZATIONS ONLY
   */
  // Get price and dimensions and elements
  const pdpPriceElement = document.querySelector(
    ".price-list span.price.price--large .price-only"
  );
  const atcPrice = document.querySelector("#AddToCart span.atc-price");
  const widthFt = document.querySelector('[name="customSizeWidthFt"]');
  const widthIn = document.querySelector('[name="customSizeWidthIn"]');
  const lengthFt = document.querySelector('[name="customSizeLengthFt"]');
  const lengthIn = document.querySelector('[name="customSizeLengthIn"]');
  const minSqFt = document.querySelector('[name="minTotalSqFt"]');
  const maxWidthFt = document.querySelector('[name="maxWidthFt"]');
  const maxWidthIn = document
    .querySelector('[name="maxWidthFt"]')
    .getAttribute("inches-max-width");
  const pristineCheck = document.querySelector(
    '[type="checkbox"][name="properties[Add pristine rug treatment]"]'
  );
  const rugPadCheck = document.querySelector(
    '[type="checkbox"][name="properties[Add compatible rug pad]"]'
  );
  const priceList = document.querySelector("[data-product-price-list]");
  const atcButton = document.querySelector("#AddToCart");
  const outerPriceElements = document.querySelectorAll(".price-list > .price");
  const mainProductAlertElement = document.querySelector(
    "div#main > .shopify-section--main-product > section > .container > .alert.banner--error"
  );
  const errWidth = document.getElementsByClassName("alert width")[0];
  const errLength = document.getElementsByClassName("alert length")[0];
  const updateAddonsPriceElementsList = [
    widthFt,
    widthIn,
    lengthFt,
    lengthIn,
    priceList,
    atcButton,
  ];

  addDimensionChangeEventListener(widthFt);
  addDimensionChangeEventListener(widthIn);
  addDimensionChangeEventListener(lengthFt);
  addDimensionChangeEventListener(lengthIn);
  pristineCheck.addEventListener("change", updateCustomPricesListener);
  rugPadCheck.addEventListener("change", updateCustomPricesListener);
  inputSavedDimensions();

  function inputSavedDimensions() {
    let sizeSelectionLocal = localStorage.getItem("sizeSelection");
    if (!sizeSelectionLocal) {
      return;
    }
    let storedSize = JSON.parse(sizeSelectionLocal);

    let maxWidthInches = parseInt(maxWidthIn);
    let minAreaInches = parseFloat(minSqFt.value) * 144;

    let selectedWidthTotalInches = storedSize['total_width_inches'];
    if (!Number.isNaN(maxWidthInches) && selectedWidthTotalInches > maxWidthInches) {
      selectedWidthTotalInches = maxWidthInches;
    }

    let selectedLengthTotalInches = storedSize['total_length_inches'];
    let selectedArea = selectedWidthTotalInches * selectedLengthTotalInches;
    if (!Number.isNaN(minAreaInches) && selectedArea < minAreaInches) {
      selectedLengthTotalInches = Math.ceil(minAreaInches / selectedWidthTotalInches);
    }

    widthFt.value = Math.floor(selectedWidthTotalInches / 12);
    widthIn.value = selectedWidthTotalInches % 12;
    lengthFt.value = Math.floor(selectedLengthTotalInches / 12);
    lengthIn.value = selectedLengthTotalInches % 12;

    lengthIn.dispatchEvent(new Event('input'));
  }

  // Run updateCustomPricesListener post-variants:changed
  const priceListMutationObserver = addPostVariantChangedEventListener(
    priceList,
    outerPriceElements,
    priceListIsTargetMutation,
    priceListReAddNode,
    updateCustomPricesListener
  );
  const atcPriceMutationObserver = addPostVariantChangedEventListener(
    atcPrice.parentElement,
    atcPrice,
    (mutation, preservedElements) => {
      if (mutation.target.contains(preservedElements)) {
        return false;
      }
      for (const removedNode of mutation.removedNodes.values()) {
        if (
          removedNode
            .cloneNode(false)
            .isEqualNode(preservedElements.cloneNode(false))
        ) {
          return removedNode;
        }
      }
      return false;
    },
    (mutation, removedNodes) => mutation.target.appendChild(removedNodes),
    updateCustomPricesListener
  );

  setDefaultAddOnPrice("pristine", productForm);
  setDefaultAddOnPrice("rug-pad", productForm);

  /** Event listeners **/
  async function addDimensionChangeEventListener(el) {
    el.addEventListener("input", updateCustomPricesListener);
  }

  function addPostVariantChangedEventListener(
    observedElement,
    preservedElements,
    isTargetMutation,
    reAddNodes,
    dispatchEventCallback = () => null,
    config = { childList: true }
  ) {
    const eventName = "variant:change-custom-price";
    const observer = new MutationObserver((mutationList, observer) => {
      toggleCompareAndCustomClassHide(false);

      let shouldDispatch = false;
      let targetMutation, removedNodes;
      for (const mutation of mutationList) {
        removedNodes = isTargetMutation(mutation, preservedElements);
        if (removedNodes) {
          targetMutation = mutation;
          break;
        }
      }
      if (removedNodes) {
        shouldDispatch = reAddNodes(targetMutation, removedNodes);
      }
      if (shouldDispatch) {
        const variantChangeCustomPrice = new Event(eventName);
        observedElement.dispatchEvent(variantChangeCustomPrice);
      }
    });
    observer.observe(observedElement, config);
    observedElement.addEventListener(eventName, dispatchEventCallback);
    return observer;
  }

  function priceListIsTargetMutation(mutation, preservedElements) {
    if (
      Array.from(preservedElements.values()).includes((el) =>
        mutation.target.contains(el)
      )
    ) {
      return false;
    }
    if (mutation.removedNodes.length > 0) {
      return mutation.removedNodes;
    }
    return false;
  }

  function priceListReAddNode(mutation, removedNodes) {
    const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
    mutation.target.innerHTML = "";
    removedNodes.forEach((node) => {
      if (!(node instanceof Element)) {
        return;
      }
      const priceOnlySpan = node.querySelector(".price-only");
      if (priceOnlySpan) {
        const updatedVariantPricing = getCustomPricing(variantPricing.price);
        mutation.target.append(node);
        return;
      }
      const priceCompareSpan = node.querySelector("span.price.price--compare");
      if (!priceCompareSpan) {
        return;
      }
      const priceCompareTextNode = Array.from(
        priceCompareSpan.childNodes.values()
      ).find(
        (value) =>
          value.nodeName === "#text" &&
          value.nodeValue.match(/\$[\d,]+\.\d{2}/) !== null
      );
      priceCompareTextNode.nodeValue = `${formatMoney(
        variantPricing.compare_at_price,
        currencyFormat
      )} `;
      mutation.target.append(node);
      return;
    });
    return true;
  }

  submitButton.addEventListener("click", async function (event) {
    if (event.preventLoop) {
      return;
    }

    const { target } = event;
    const originalEvent = new event.constructor(event.type, event);
    originalEvent.preventLoop = true;
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    /** Custom Size Variant ProductForm workflow **/
    const isCustomSize = getIsCustomSize();
    console.log("isCustomSize=" + isCustomSize);
    if (!isCustomSize) {
      target.dispatchEvent(originalEvent);
      return false;
    }

    try {
      const variantId = await getCustomSizeVariant(productForm);
      console.log(`getCustomSizeVariant() returned variantId: ${variantId}`);
      if (!variantId) {
        throw new CustomRugApiError(
          window.themeVariables.strings.errorCustomRugApi,
          { cause: { code: "NoVariantId" } }
        );
      }

      await setTimeout(async () => {
        const responseJson = await addProduct(variantId, productForm);
        console.log("added product:");
        console.log(responseJson);

        rebuildCart(productForm);
        showAddToCartNotification();
      }, 600);
    } catch (e) {
      console.log(e.message);

      toggleFormSubmitButtons(productForm, false, false);
      if (e instanceof PriceApiError) {
        displayMainProductError(e.message);
      } else if (e instanceof CustomRugApiError) {
        displayMainProductError(e.message);
      } else {
        displayMainProductError(
          window.themeVariables.strings.errorProductGeneral
        );
      }
      return;
    }
  });

  async function showAddToCartNotification() {
    await setTimeout(async () => {
      productForm.dispatchEvent(
        new CustomEvent("cart-notification:show", {
          bubbles: true,
          cancelable: true,
          detail: {
            status: "success",
            error: "",
          },
        })
      );
    }, 600);
  }

  /** Price-related functions **/
  // js/helper/currency.js from theme.js for consistent configured values
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
      formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."),
        dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands
        ),
        centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

  function formatMoneyInDollars(dollars) {
    return formatMoney(dollars * 100, "");
  }

  async function setDefaultAddOnPrice(type, productForm) {
    const productFormData = new FormData(productForm);
    const currentVariant = await getActiveVariant(mainProduct, productFormData);
    const addOnBasePrice = await getVariantBaseAddOnPrice(currentVariant, type);
    const addOnPriceEl = document.querySelector('[class="' + type + '-price"]');
    addOnPriceEl.innerHTML = addOnBasePrice + " per sq. ft.";
  }

  async function getCurrentVariantAddOnPrice(type) {
    const addOnPriceEl = document.querySelector('[class="' + type + '-price"]');
    return parseFloat(
      addOnPriceEl.innerHTML
        .replace("$", "")
        .replace(" per sq. ft.", "")
        .replace(" USD", "")
    );
  }

  async function setCurrentVariantAddOnPrice(type, value) {
    const addOnPriceEl = document.querySelector('[class="' + type + '-price"]');
    addOnPriceEl.innerHTML = formatMoney(value * 100);
  }

  async function getVariantBaseAddOnPrice(variant, type) {
    const addOnBasePriceEl = document.querySelector(
      '[name="' + variant.id + "_" + type + '_base_price"]'
    );
    return formatMoney(addOnBasePriceEl.value);
  }

  async function getCurrentAddOnPrices() {
    const pristinePrice = await getCurrentVariantAddOnPrice("pristine");
    const rugPadPrice = await getCurrentVariantAddOnPrice("rug-pad");
    return {
      pristinePrice,
      rugPadPrice,
      totalPrice: pristinePrice + rugPadPrice,
    };
  }

  function toggleCompareAndCustomClassHide(hide = true) {
    const hideClass = "visually-hidden";
    const priceListContainer = document.querySelector(
      ".product__info > product-meta > div .product-meta__price-list-container"
    );
    const nonApplicableToCustomSizePricingSelectors = [
      ".price-list .price > .price-custom-prefix",
      ".price-list .price > .price-custom-postfix",
      ".price-list .price > span.price.price--compare",
      ".price-list .price > .price-custom-prefix.price--compare",
      "[data-product-label-list]",
    ];
    for (const selector of nonApplicableToCustomSizePricingSelectors) {
      const priceElementToHide = priceListContainer.querySelector(selector);
      if (!priceElementToHide?.classList) {
        continue;
      }
      priceElementToHide.classList.toggle(hideClass, hide);
    }
  }

  async function updateCustomPrices(rugPrice, pristinePrice, paddingPrice) {
    let pristinePriceDollars = pristinePrice * 100;
    let paddingPriceDollars = paddingPrice * 100;
    let rugPriceDollars = rugPrice * 100;
    let newPrice = pristinePriceDollars + paddingPriceDollars + rugPriceDollars;

    let newPriceString = `${formatMoney(newPrice)} USD`;
    pdpPriceElement.innerHTML = newPriceString;
    atcPrice.innerHTML = " - " + newPriceString;

    // Remove initial pricing
    toggleCompareAndCustomClassHide();

    if (pristinePrice !== 0) {
      setCurrentVariantAddOnPrice("pristine", pristinePrice);
    }
    if (paddingPrice !== 0) {
      setCurrentVariantAddOnPrice("rug-pad", paddingPrice);
    }
  }

  async function updateCustomPricesListener(event) {
    let isValidDimensions = validateCustomDimensionsInput();
    let currentColorways = document.querySelector(
      '.product-form__option-selector[data-selector-type="color"] .product-form__option-value'
    ).innerText;
    if (!isValidDimensions) {
      return;
    }

    try {
      // Call getProductFormData() runs getRugCalculationPricing() updates prices
      await getProductFormData(productForm);
      const { pristinePrice, rugPadPrice } = await getCurrentAddOnPrices();
      
      if (currentColorways.length) {
        toggleFormSubmitButtons(productForm, true);
      }

      if (!updateAddonsPriceElementsList.includes(event.target)) {
        return;
      }

      if (pristinePrice === 0) {
        setDefaultAddOnPrice("pristine", productForm);
      }
      if (rugPadPrice === 0) {
        setDefaultAddOnPrice("rug-pad", productForm);
      }
    } catch (e) {
      console.log(e.message);
      
      if(currentColorways.length) {
        toggleFormSubmitButtons(productForm, true);
      }

      if (e instanceof PriceApiError) {
        displayMainProductError(e.message);
      } else {
        displayMainProductError(
          window.themeVariables.strings.errorProductGeneral
        );
      }
    }
  }

  Number.prototype.round = function (places) {
    return +(Math.round(this + "e+" + places) + "e-" + places);
  };

  /** Dimensions-related functions **/
  function getDimensions(formData) {
    let isValidDimensions = validateCustomDimensionsInput();
    if (!isValidDimensions) {
      errLength.innerHTML = "Cannot get dimensions when sizes are not set";
    }
    const customSizeWidthFt = formData.get("customSizeWidthFt");
    const customSizeWidthIn = formData.get("customSizeWidthIn");
    const customSizeLengthFt = formData.get("customSizeLengthFt");
    const customSizeLengthIn = formData.get("customSizeLengthIn");
    const length = getTotalInches(customSizeLengthFt, customSizeLengthIn);
    const width = getTotalInches(customSizeWidthFt, customSizeWidthIn);

    return { length, width };
  }

  function getTotalInches(feet, inches) {
    const feetInInchesNumber = (Number(feet) || 0) * 12;
    if (feetInInchesNumber < 12) {
      errLength.innerHTML =
        "Rugs are expected to have dimensions with a minimum of 1 foot.";
    }

    const inchesNumber = Number(inches);
    if (inchesNumber < 1) {
      return feetInInchesNumber.toString();
    }

    const totalInches = feetInInchesNumber + inchesNumber;

    return totalInches.toString();
  }

  function validateCustomDimensionsInput() {
    const ftWide = widthFt.valueAsNumber,
      inWide = widthIn.valueAsNumber,
      ftLong = lengthFt.valueAsNumber,
      inLong = lengthIn.valueAsNumber,
      validateCondition = () =>
        !isNaN(ftWide) && ftWide > 0 && !isNaN(ftLong) && ftLong > 0;

    let currentColorways = document.querySelector(
      '.product-form__option-selector[data-selector-type="color"] .product-form__option-value'
    ).innerText;

    validateSelections();

    let totalWidthIn = 0;
    if (ftWide) {
      totalWidthIn += ftWide * 12;
    }
    if (inWide) {
      totalWidthIn += inWide;
    }

    let totalLengthIn = 0;
    if (ftLong) {
      totalLengthIn += ftLong * 12;
    }
    if (inLong) {
      totalLengthIn += inLong;
    }
    const totalAreaIn = totalWidthIn * totalLengthIn;

    if (!minSqFt || !maxWidthFt) {
      return validateCondition();
    }

    if (totalAreaIn < minSqFt.value * 144) {
      errLength.innerHTML =
        "Minimum total area is " + minSqFt.value + " sq. feet";
        atcButton.setAttribute("disabled", "");
      if (!lengthFt.classList.contains("error-border")) {
        lengthFt.classList.add("error-border");
      }
      if (!lengthIn.classList.contains("error-border")) {
        lengthIn.classList.add("error-border");
      }
    } else {
      errLength.innerHTML = "";
      lengthFt.classList.remove("error-border");
      lengthIn.classList.remove("error-border");
    }

    if (totalWidthIn <= 0) {
      errWidth.innerHTML = "Please enter a valid width";
    } else if (totalWidthIn > maxWidthIn) {
      errWidth.innerHTML = "Maximum width is " + maxWidthFt.value + " feet";
    } else {
      errWidth.innerHTML = "";
    }

    if (totalWidthIn <= 0 || totalWidthIn > maxWidthIn) {
      if (!widthFt.classList.contains("error-border")) {
        widthFt.classList.add("error-border");
      }
      if (!widthIn.classList.contains("error-border")) {
        widthIn.classList.add("error-border");
      }
    } else {
      widthFt.classList.remove("error-border");
      widthIn.classList.remove("error-border");
    }

    const hasWidthInput = !isNaN(ftWide) || !isNaN(inWide);
    const hasLengthInput = !isNaN(ftLong) || !isNaN(inLong);

    if (
      currentColorways.length !== 0 &&
      errWidth.innerHTML.length === 0 &&
      errLength.innerHTML.length === 0 &&
      hasWidthInput &&
      hasLengthInput
    ) {
      atcButton.removeAttribute("disabled");
    } else {
      atcButton.setAttribute("disabled", "");
    }

    if (totalAreaIn < minSqFt.value * 144) {
      return false;
    }
    return validateCondition();
  }

  /** Mini cart functions **/
  async function rebuildCart(productForm) {
    fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
      async (response2) => {
        const cartContent = await response2.json();

        productForm.dispatchEvent(
          new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent,
            },
          })
        );

        productForm.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart:
                window.themeVariables.settings.cartType === "drawer" &&
                this.closest(".drawer") === null,
            },
          })
        );
      }
    );
  }

  /** Custom app related functions **/
  function getIsCustomSize() {
    const customSizeLengthFt = document.querySelector(
      '[name="customSizeLengthFt"]'
    );
    return !(customSizeLengthFt === undefined || customSizeLengthFt === null);
  }

  // Custom Size Variant App endpoint requests
  async function getCustomSizeVariant(productForm) {
    const formData = await getProductFormData(productForm);

    if (!formData) {
      return formData;
    }

    let formDataEntries = "";
    for (const entry of formData.entries()) {
      formDataEntries +=
        (formDataEntries.length > 0 ? "," : "") +
        `"${entry[0]}": "${entry[1]}"`;
    }

    // Check for existing variant to avoid creating duplicates
    try {
      const response = await requestXhr(
        `/apps/stark-carpet/get-custom-size-variant`,
        formData
      );

      return await getResponseJson("Get Custom Size Variant", response);
    } catch (e) {
      throw new CustomRugApiError(
        window.themeVariables.strings.errorCustomRugApi,
        { cause: e }
      );
    }
  }

  /** Add to cart functions **/
  async function addProduct(variantId, productForm) {
    // Prevent /cart/add POST immediately following variant create without all Product Variant data available
    const qtyElement = document.querySelector('[name="quantity"]');
    let responseJson = null;
    let qty = 1;
    if (qtyElement !== undefined) {
      qty = qtyElement.value;
    }
    const pristinePrice = await getCurrentVariantAddOnPrice("pristine");
    const rugPadPrice = await getCurrentVariantAddOnPrice("rug-pad");
    const leadTimePrefix = "Expected to ship in";
    let leadTime = await getLeadTime();
    let pristinePriceString = formatMoney(pristinePrice * 100).toString();
    let rugPadPriceString = formatMoney(rugPadPrice * 100).toString();
    leadTime = leadTime !== null ? leadTime.replace(leadTimePrefix, "") : "";

    // Would be better to set the properties needed on the lineItemProperties object
    // than to set them, then delete them
    let lineItemProperties = {
      "+ Pristine Treatment": pristinePriceString,
      "+ Rug Pad": rugPadPriceString,
      "Expected to ship in": leadTime,
    };

    if (!pristineCheck.checked) {
      delete lineItemProperties["+ Pristine Treatment"];
    }
    if (!rugPadCheck.checked) {
      delete lineItemProperties["+ Rug Pad"];
    }
    if (leadTime === "") {
      delete lineItemProperties["Expected to ship in"];
    }

    const itemData = {
      items: [
        {
          id: variantId,
          quantity: qty,
          properties: lineItemProperties,
        },
      ],
    };

    // Add product or custom variant to cart
    responseJson = fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(itemData),
    }).then(function (response) {
      responseJson = getResponseJson("addProduct fetch response", response);
      // theme.js post-/cart/add dispatch event code
      productForm.dispatchEvent(
        new CustomEvent("variant:added", {
          bubbles: true,
          detail: {
            variant: responseJson.hasOwnProperty("items")
              ? responseJson["items"][0]
              : responseJson,
          },
        })
      );

      rebuildCart(productForm);

      return responseJson;
    });
    toggleFormSubmitButtons(productForm, true);

    // return original Add to Cart response after theme.js style post-add dispatch
    return responseJson;
  }

  function toggleFormSubmitButtons(
    productForm,
    enable = true,
    ariaBusy = true
  ) {
    const submitButtons =
      productForm.submitButtons ||
      Array.from(productForm.elements).filter(
        (button) => button.type === "submit"
      );

    submitButtons.forEach((submitButton) => {
      if (enable) {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      } else {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", ariaBusy);
      }
    });
  }

  /** Form (and other) utility functions **/
  async function getProductFormData(formElement) {
    // Get form data & determine if custom variant app needed
    const productFormData = new FormData(formElement);
    const hasPristine = getPropertiesBool(
      productFormData,
      "Add pristine rug treatment"
    );
    const hasPad = getPropertiesBool(productFormData, "Add compatible rug pad");
    const { length, width } = getDimensions(productFormData);
    const color = getSelectedColor();
    const finishing = getSelectedFinishingMethod();
    const originalSize = getOriginalSize();

    // Update form data and make request
    const listenerMainProduct = await (mainProduct !== undefined
      ? mainProduct
      : getProduct());
    const activeVariant = getActiveVariant(
      listenerMainProduct,
      productFormData
    );

    // Get Translations
    const productMeta = document.querySelector('product-meta');
    let productTranslations = null;
    
    if(productMeta){
      productTranslations = JSON.parse(productMeta.dataset.productTranslations);
    }

    // Remove extra form data
    productFormData.delete("customSizeWidthFt");
    productFormData.delete("customSizeWidthIn");
    productFormData.delete("customSizeLengthFt");
    productFormData.delete("customSizeLengthIn");
    productFormData.delete("option1");
    productFormData.delete("option2");
    productFormData.delete("option3");

    // Update data as needed to create custom variant
    const {
      rugPrice: customPrice,
      pristinePrice: pristinePrice,
      paddingPrice: paddingPrice,
    } = await getRugCalculationPricing(activeVariant, formElement, productMeta ? productTranslations.calculating_price : 'Calculating price...');
    const addOnsPricing = await getCurrentAddOnPrices();
    let customVariantPrice = parseFloat(
      customPrice + pristinePrice + paddingPrice
    );
    if (isB2b) {
      let b2bMultiplier = 1 / ((100 - b2bDiscount) / 100);
      customVariantPrice = customVariantPrice * b2bMultiplier;
    }

    // Custom Variant create required fields not in form
    productFormData.append("productId", listenerMainProduct.id);
    productFormData.append("title", listenerMainProduct.title);
    productFormData.append("price", customVariantPrice);
    productFormData.append("sku", activeVariant.sku);
    productFormData.append("color", color);
    productFormData.append("binding", finishing);
    productFormData.append("size", originalSize);
    productFormData.append("handle", listenerMainProduct.handle);
    productFormData.append("length", length);
    productFormData.append("width", width);
    productFormData.append(
      "imageId",
      activeVariant.featured_image !== null
        ? activeVariant.featured_image.id
        : 0
    );
    productFormData.append("hasPristine", hasPristine);
    productFormData.append("hasPad", hasPad);
    productFormData.append(
      "+ Pristine Treatment",
      formatMoney(addOnsPricing.pristinePrice).toString()
    );
    productFormData.append(
      "+ Rug Pad",
      formatMoney(addOnsPricing.rugPadPrice).toString()
    );
    productFormData.append("Expected to ship in", await getLeadTime());

    return productFormData;
  }

  function getFormSubmitButton(form) {
    return form.querySelector('[type="submit"]');
  }

  function getProductForm(product) {
    const productForms = document.querySelectorAll("form[is='product-form']"); // "form[action='cart/add']" may be more portable between themes
    for (const form of productForms) {
      const formId = form.attributes.id.value;
      if (formId.includes(product.id)) {
        return form;
      }
    }
    throw new Error(`No form for product ID '${product.id}' found`);
  }

  function getPropertiesBool(formData, propertyName) {
    const targetValues = formData.getAll(`properties[${propertyName}]`);
    const lastValue = targetValues.pop();

    return lastValue === "Yes";
  }

  function getActiveVariant(product, formData) {
    const variantId = formData.get("id");
    return product.variants.find(
      (variant) => variant.id.toString() === variantId
    );
  }

  async function getResponseJson(type, response) {
    console.log(`getResponseJson('${type}', {Response})`);
    if (!response || response.status !== 200) {
      throw new ApiError(window.themeVariables.strings.errorApi, {
        cause: {
          status: response?.status || null,
          statusText: response?.statusText || null,
          response: response,
          getResponseJsonType: type,
        },
      });
    }
    try {
      return await response.json();
    } catch (e) {
      console.error(`getResponseJson caught exception:`, e);
      return null;
    }
  }

  async function getProduct() {
    const pagePath = window.location.pathname;
    if (!pagePath.startsWith("/products/")) {
      throw new Error("Not a product page, cannot get Product");
    }
    const response = await fetch(`${pagePath}.js`);
    return await getResponseJson("getProduct", response);
  }

  function displayMainProductError(message) {
    if (!mainProductAlertElement) {
      return;
    }
    const hide = !message || message?.trim().length === 0;
    mainProductAlertElement.innerHTML = message;
    mainProductAlertElement.classList.toggle("visually-hidden", hide);
  }

  function clearMainProductErrors() {
    displayMainProductError("");
  }

  /** Get product options and selections functions **/
  function getColorOption() {
    return document.querySelector(
      ".color-swatch-list > .color-swatch > input:checked"
    );
  }

  function getSelectedColor() {
    let colorOption = getColorOption();
    if (colorOption === undefined || colorOption === null) {
      throw new Error("Color option missing or no option selected");
    }
    return colorOption.value;
  }

  function getFinishingMethodOption() {
    return document.querySelector('[aria-label="Finishing"]');
  }

  function getSelectedFinishingMethod() {
    // Expected binding options: “serging”, “binding”, “widebinding”, “handserging”
    let finishingOption = getFinishingMethodOption();
    if (finishingOption === undefined || finishingOption === null) {
      return "Serging"; // TODO: confirm default option to pass to API
    }

    return finishingOption.value;
  }

  function getOriginalSizeOption() {
    return document.querySelector('[aria-label="Size"]');
  }

  function getOriginalSize() {
    let sizeOption = getOriginalSizeOption();
    if (sizeOption !== undefined && sizeOption !== null) {
      return sizeOption.value;
    }
    console.log("No size option");
    return sizeOption;
  }

  function getLeadTime() {
    let leadTime = document.querySelector('[class="lead-time-label"]');
    if (leadTime !== undefined && leadTime !== null && leadTime !== "") {
      return leadTime.innerHTML;
    }
    return null;
  }

  // API Errors
  class ApiError extends Error {
    constructor(message) {
      super(message);
      this.name = "ApiError";
    }
  }

  class PriceApiError extends ApiError {
    constructor(message) {
      super(message);
      this.name = "PriceApiError";
    }
  }

  class CustomRugApiError extends ApiError {
    constructor(message) {
      super(message);
      this.name = "CustomRugApiError";
    }
  }

  /** Rug Fabrication API function **/
  async function getRugCalculationPricing(variant, formElement, calculatingTranslation) {
    const formData = new FormData(formElement);
    const hasPristine = getPropertiesBool(
      formData,
      "Add pristine rug treatment"
    );
    const hasPad = getPropertiesBool(formData, "Add compatible rug pad");
    const variantSku = await prepVariantSkuForFabricationCalc(variant.sku);
    const { length, width } = getDimensions(formData);
    let finishingMethod = getSelectedFinishingMethod(formElement);

    // Transform to comply with API's expected values
    finishingMethod = finishingMethod.replace(/\s/g, "");

    console.log("SKU passed to Rug Fabrication API: " + variantSku);
    pdpPriceElement.innerText = calculatingTranslation;
    const { CalculationValues, CalculationCharge } =
      await requestRugCalculation(
        variantSku,
        finishingMethod,
        width,
        length,
        hasPristine,
        hasPad
      );
    clearMainProductErrors();
    let rugPrice =
      parseFloat(CalculationCharge.RetailCharge.CarpetCharge) +
      parseFloat(CalculationCharge.RetailCharge.FabricationCharge);
    if (isB2b) {
      rugPrice =
        parseFloat(CalculationCharge.TradeCharge.CarpetCharge) +
        parseFloat(CalculationCharge.TradeCharge.FabricationCharge);
    }
    const { Total: pristinePrice } = CalculationValues.find(
      (value) => value.Type === "Pristine"
    );
    const { Total: paddingPrice } = CalculationValues.find(
      (value) => value.Type === "Padding"
    );
    console.log(
      `getRugCalculationPricing API - Rug Price: ${rugPrice},`,
      `Pristine Price: ${formatMoney(pristinePrice * 100)}`,
      `Padding Price: ${formatMoney(paddingPrice * 100)}`
    );

    updateCustomPrices(rugPrice, pristinePrice, paddingPrice);

    return { rugPrice, pristinePrice, paddingPrice };
  }

  // NOTE: May need to set default values for specific listeners:
  // i.e. our custom Product Variant submit "click" listener and the theme "variant:changed"
  async function requestRugCalculation(
    sku,
    finishingMethod,
    width,
    length,
    hasPristine,
    hasPad
  ) {
    const request = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        SKU: sku.replace("-", " "),
        BindingMethod: finishingMethod,
        WidthInInches: width,
        LengthInInches: length,
        IsPristineTreatment: hasPristine,
        IsAddingPadding: hasPad,
      }),
    };
    // TODO: Move API URL and path to settings outside of committed code
    // Comment the initial values here with location of settings for ease of maintenance
    try {
      const calcResponse = await fetch(
        "https://pmapapi.prestigemills.com/ViewItem/RugCalculation",
        request
      ).then(
        async (response) =>{
          validateCustomDimensionsInput();
          return await getResponseJson("requestRugCalculation() fetch", response)
        }
      );
      if (calcResponse === null) {
        throw new Error(`Request Rug Calculation null response exception`);
      }
      console.log("Response from Fabrication Calculator API:");
      console.log(calcResponse);
      return calcResponse;
    } catch (e) {
      throw new PriceApiError(window.themeVariables.strings.errorPriceApi, {
        cause: e,
      });
    }
  }

  async function prepVariantSkuForFabricationCalc(rawSku) {
    // Transform sku as needed for Fabrication Calculator API
    return rawSku
      .replace("_Binding", "")
      .replace("_Wide-Binding", "")
      .replace("_Serging", "")
      .replace("_Hand-Serging", "");
  }

  async function requestXhr(path, formData) {
    const config = {
      method: "POST",
      headers: {
        "ngrok-skip-browser-warning": true,
        "X-Requested-With": "XMLHttpRequest",
        Accept: `application/json`,
        "x-shop-domain":
          window.Shopify.shop === "starkcarpet.myshopify.com"
            ? "starkcarpet.com"
            : "stark-carpet-development.myshopify.com",
      },
      body: formData,
    };

    return await fetch(path, config);
  }

  function updateVariantData(){
    const initialSelections = document.querySelectorAll('[data-initial-selection]');
    if(!initialSelections) return;

    initialSelections.forEach(selection => {
      selection.removeAttribute('data-initial-selection');
    });
    validateSelections();
  }

  /* Sample Ordering functions */
  function updateSampleOrderingOptions() {
    const hideClass = "visually-hidden";
    const isB2bEl = document.querySelector(".customer-type-data");
    const isB2b = isB2bEl !== undefined ? isB2bEl.innerHTML === "true" : false;
    const orderSampleCTA = document.querySelector(
      ".product-form__sample-rug-button"
    );
    const currentColorWaysContainer = document.querySelector('.color-swatch__radio[checked=checked]');
    const currentColorways = currentColorWaysContainer ? currentColorWaysContainer.value : null;
    let variantSamplesDataEl = document.querySelector(".variant-samples-data");
    let variantSamplesData =
      variantSamplesDataEl !== undefined ? variantSamplesDataEl.innerHTML : "";
    let allVariantSamples = variantSamplesData.split("|");
    let thisVariantSamples = [];

    Object.keys(allVariantSamples).forEach((i) => {
      if (allVariantSamples[i] !== "") {
        let currentVariantSamples = allVariantSamples[i].split(":");
        let currentVariantId = currentVariantSamples[0];
        let thisVariantSampleXData = currentVariantSamples[1].split(",");
        let thisVariantSku = thisVariantSampleXData[0];
        let thisVariantAvailability = thisVariantSampleXData[1] === "true";
        let thisVariantColorways = thisVariantSampleXData[2];
        // If available and visible to the customer type (B2B or B2C), we'll show the Order a Sample button
        if (
          thisVariantAvailability === true &&
          currentColorways === thisVariantColorways
        ) {
          if (thisVariantSku.indexOf("_b2b") !== -1 && isB2b) {
            thisVariantSamples.push(currentVariantId);
          } else if (thisVariantSku.indexOf("_b2b") === -1 && !isB2b) {
            thisVariantSamples.push(currentVariantId);
          }
        }
      }
    });
    // Show / Hide "Order a Sample" button as appropriate on variant change
    if (thisVariantSamples.length > 0) {
      orderSampleCTA.classList.toggle(hideClass, false);
    } else {
      orderSampleCTA.classList.toggle(hideClass, true);
    }
  }
})();
